import React, { ChangeEvent, useEffect, useState } from 'react';
import { Grid, Button, Tooltip } from '@material-ui/core';
import { ModalWrapper } from 'components/Modals';
import TokenIcon from 'assets/images/token.svg';
import LlamaIcon from 'assets/images/llama.svg';
import { TextField } from '@material-ui/core';
import { useSelector, useDispatch } from 'react-redux';
import { RootStore } from 'redux/store';
import { LoaderButton } from 'components/LoaderButton';
import { sendFeedback } from 'redux/thunks/user';
import './FeedbackModal.scss';
import { Feedback } from 'redux/models/feedback';
import { resetSendFeedbackSuccess } from 'redux/actions';
import { useEasterEggFound } from 'helpers/easterEgghelper';
import EASTER_EGG_IDS from 'helpers/easterEggConstants';

interface FeedbackModalProps {
	open: boolean;
	onCloseClicked: () => void;
	allowClickOutsideToClose?: boolean;
}

const FeedbackModal: React.FC<FeedbackModalProps> = (props: FeedbackModalProps) => {
	const { open, allowClickOutsideToClose, onCloseClicked } = props;
	const [body, setBody] = useState('');
	const [title, setTitle] = useState('');
	const [titleError, setTitleError] = useState(false);
	const [bodyError, setBodyError] = useState(false);
	const [llamaTime, setLlamaTime] = useState(false);
	const easterEggFound = useEasterEggFound(EASTER_EGG_IDS.LLAMA_TIME);

	const userEmail = useSelector((state: RootStore) => state.user.userEmail);
	const feedbackSending = useSelector((state: RootStore) => state.user.feedbackSending);
	const feedbackSendingSuccess = useSelector((state: RootStore) => state.user.feedbackSendingSuccess);
	const dispatch = useDispatch();

	useEffect(() => {
		if (feedbackSendingSuccess) {
			dispatch(resetSendFeedbackSuccess());
			onCloseClicked();
		}
	}, [feedbackSendingSuccess, dispatch, onCloseClicked]);

	useEffect(() => {
		if (open) {
			setTitle('');
			setBody('');
		}
	}, [open]);

	const send = () => {
		if (!validateFeedback()) {
			return;
		}

		const newFeedback: Feedback = {
			title: title,
			body: body,
		};

		dispatch(sendFeedback(newFeedback));
	};

	const toggleLammaTime = () => {
		setLlamaTime(!llamaTime);
		if (!llamaTime) {
			easterEggFound();
		}
	};

	const validateFeedback = () => {
		let valid = true;

		if (body.length < 1) {
			valid = false;
			setBodyError(true);
		} else {
			setBodyError(false);
		}

		if (title.length < 1) {
			valid = false;
			setTitleError(true);
		} else {
			setTitleError(false);
		}

		return valid;
	};

	const setBodyValue = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		setBody(event.target.value);
	};

	const setTitleValue = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		setTitle(event.target.value);
	};

	return (
		<ModalWrapper open={open} onCloseClicked={onCloseClicked} allowClickOutsideToClose={allowClickOutsideToClose}>
			<Grid container direction='column' alignItems='center' className='feedback-modal'>
				<Tooltip title='🥚'>
					<img
						src={TokenIcon}
						onClick={() => toggleLammaTime()}
						className={`${llamaTime ? 'token-icon transparent' : 'token-icon'}`}
						alt='toa-token'
					/>
				</Tooltip>

				<img src={LlamaIcon} onClick={() => toggleLammaTime()} className={`${llamaTime ? 'token-icon' : 'token-icon transparent'}`} alt='toa-token' />

				<div className='toa-title'>Feedback</div>

				<Grid container item>
					<Grid item xs={6}>
						<TextField label='Email' variant='outlined' fullWidth required disabled value={userEmail} />
					</Grid>
					<Grid item xs={6} className='feedback-modal__text-item--padding-left'>
						<TextField
							value={title}
							error={titleError}
							onChange={event => setTitleValue(event)}
							label='Title'
							variant='outlined'
							fullWidth
							required
							helperText='Required'
						/>
					</Grid>
					<Grid item xs={12} className='feedback-modal__text-item'>
						<TextField
							value={body}
							error={bodyError}
							onChange={event => setBodyValue(event)}
							multiline
							rowsMax={6}
							rows={6}
							label='Body'
							variant='outlined'
							helperText='Required'
							fullWidth
							required
						/>
					</Grid>
				</Grid>

				<div className='button-container'>
					<Button variant='contained' color='primary' className='cancel-button' onClick={onCloseClicked}>
						cancel
					</Button>
					<LoaderButton type='button' loading={feedbackSending} disabled={feedbackSending} onHandleClick={send}>
						Send
					</LoaderButton>
				</div>
			</Grid>
		</ModalWrapper>
	);
};

export default FeedbackModal;
